import React from "react"
import {Checkbox, Collapse, CollapseProps} from "antd"
import {v4 as uuid} from "uuid"
import {isBoolean} from "lodash"
import moment from "moment"
import {withTranslation} from "react-i18next"
import {Model} from "Model"
import {BaseInput} from "components"
import {handleError, toastError, toastWarning} from "helpers"
import {shiftsService} from "services"
import {KlassappTableHOC} from "HOC"
import {KlassappTable, KlassappTableContext, KlassappTableHeader} from "uiKit"
import {BaseButton, SecondaryButton, AddItemCircleButton} from "components/buttons"
import {KlassappTableProps} from "types/common"
import styles from "./Shifts.module.css"
import {BaseTimePicker} from "components/DateTimePicker"
import CampusSelect from "components/CampusSelect"
import ProgramSelect from "components/ProgramSelect"
import {getDisabledEndHours, getDisabledEndMinutes} from "helpers/calendar"
import {INITIAL_WORK_WEEK, WEEKDAY_NAMES, WorkDay} from "types/work-schedule"
import {WorkDayItem} from "sections/CalendarSettings/parts/WorkScheduleDetails/parts"
import ProgramVersionSelect from "components/ProgramVersionSelect"

const {Panel} = Collapse

type PageProps = {
    t: Function
    model: Model
}

type Props = KlassappTableProps & PageProps

type State = {
    newItemData: any
    oldItemData: any
    isSubmitting: boolean
}

class ShiftTab extends React.Component<Props, State> {
    state = {
        newItemData: null,
        oldItemData: null,
        isSubmitting: false
    }

    componentDidMount() {
        this.props.dispatchFunc([
            {key: "getPageTitle", func: this.getPageTitle},
            {key: "getListData", func: this.getSettings},
            {key: "getFields", func: this.getFields},
            {key: "getColumns", func: this.getColumns},
            {key: "getTableHeaderActions", func: this.getTableHeaderActions},
            {key: "getMenuActions", func: this.getMenuActions},
            {key: "onClickDeleteMulti", func: this.onClickDeleteMulti},
            {key: "onClickEdit", func: this.onClickEdit},
            {key: "onClickDelete", func: this.onClickDelete}
        ])
        this.getSettings()
    }

    getPageTitle = () => {
        return this.props.t("settings.shifts")
    }

    getFields = () => {
        const {t} = this.props
        return [
            t("settings.name"),
            t("settings.isActive"),
            t("settings.campuses"),
            t("settings.programs"),
            t("settings.programVersions")
        ]
    }

    getTableHeaderActions = (isShowDuplicateBtn = false, checkedData = []) => {
        const hasLockedItem = (checkedData || []).some((item) => item.isLock)
        const {t, onClickShowConfirmModal} = this.props
        const actions = []
        if (!hasLockedItem) {
            actions.push({
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            })
        }
        return actions
    }

    getMenuActions = () => {
        const {t} = this.props
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: this.onClickEdit,
                canShow: () => {
                    return true
                }
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: this.onClickDelete,
                canShow: () => {
                    return true
                }
            }
        ]
    }

    getSettings = async () => {
        this.props.dispatch({isLoading: true})
        try {
            const {data, total} = await shiftsService.getAll({
                linkedObject: true
            })
            const timeFormat = this.props.model.getUserTimeFormat()
            const todayStr = moment().format("YYYY-MM-DD")
            const newData = data.map((item) => {
                const _workWeek = WEEKDAY_NAMES.map((_, index) => {
                    let value = item.workWeek?.find((x) => x.dayOfWeek === index)
                    if (value) {
                        value = {
                            ...value,
                            name: WEEKDAY_NAMES[index],
                            enabled: true,
                            startTime: value.startTime ? moment(todayStr + " " + value.startTime) : undefined,
                            endTime: value.endTime ? moment(todayStr + " " + value.endTime) : undefined,
                            allDay: value.allDay === 1
                        }
                    } else {
                        value = {
                            dayOfWeek: index,
                            name: WEEKDAY_NAMES[index],
                            enabled: false,
                            allDay: false
                        }
                    }
                    return value
                })
                item.workWeek = _workWeek
                item.id = item.shiftId
                item.isActiveHtml = this.renderIsActiveHtml(item)
                item.nameHtml = this.renderName(item.name, item.workWeek)
                item.startTimeHtml = item.startTime ? moment(todayStr + " " + item.startTime).format(timeFormat) : null
                item.endTimeHtml = item.endTime ? moment(todayStr + " " + item.endTime).format(timeFormat) : null
                item.programsHtml = this.renderProgramsInput(item.programs)
                item.programVersionsHtml = this.renderProgramVersionsInput(item.programVersions)
                item.campusesHtml = this.renderCampusesInput(item.campuses)
                return item
            })
            this.props.dispatch({data: newData, total})
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    renderIsActiveHtml = (item = null) => {
        if (item) {
            return (
                <div className={styles.inputWrap}>
                    <div key={`show-${item.shiftId}`} className={styles.visibleCheckboxWrap}>
                        <Checkbox disabled checked={item.isActive} />
                    </div>
                </div>
            )
        }
        const newItemData = this.state.newItemData || {}
        return (
            <div className={styles.inputWrap}>
                <div key={`edit-${newItemData.shiftId}`} className={styles.visibleCheckboxWrap}>
                    <Checkbox
                        checked={newItemData.isActive}
                        onChange={(event) => this.onChangeNewItemData("isActive", event.target.checked)}
                    />
                </div>
            </div>
        )
    }

    getColumns = () => {
        const {t} = this.props

        return [
            {title: t("settings.name"), field: "nameHtml"},
            {title: t("settings.isActive"), field: "isActiveHtml"},
            {title: t("settings.campuses"), field: "campusesHtml", style: {minWidth: "250px"}},
            {title: t("settings.programs"), field: "programsHtml", style: {minWidth: "250px"}},
            {title: t("settings.programVersions"), field: "programVersionsHtml", style: {minWidth: "250px"}}
        ]
    }

    onClickDeleteMulti = async () => {
        const checkedItems = this.props.data.filter((item) => item.isChecked)
        const payload = checkedItems.map((item) => item.shiftId)
        try {
            this.props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await shiftsService.delete({ids: payload})
            await this.getSettings()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onClickEdit = (editItem) => {
        const {newItemData} = this.state
        if (newItemData) {
            toastWarning(this.props.t("common:validation.saveBeforeEdit"))
            return
        }
        this.setState(
            {
                newItemData: {
                    ...editItem,
                    nameData: editItem.name,
                    startTime: editItem.startTime,
                    endTime: editItem.endTime,
                    isActive: !!editItem.isActive
                },
                oldItemData: {...editItem}
            },
            () => {
                const newData = this.props.data.map((item) => {
                    if (item.shiftId === editItem.shiftId) {
                        item.isActiveHtml = this.renderIsActiveHtml()
                        item.nameHtml = this.renderNameInput()
                        item.startTimeHtml = this.renderTimeInput("startTime")
                        item.endTimeHtml = this.renderTimeInput("endTime")
                        item.programsHtml = this.renderProgramsInput()
                        item.campusesHtml = this.renderCampusesInput()
                        item.programVersionsHtml = this.renderProgramVersionsInput()
                        return item
                    }
                    return item
                })
                this.props.dispatch({data: newData})
            }
        )
    }

    onClickDelete = async (deletedItem) => {
        if (deletedItem.isForm) {
            this.setState({newItemData: null, oldItemData: null})
            await this.getSettings()
            return
        }
        try {
            this.props.dispatch({isLoading: true})
            this.setState({newItemData: null, oldItemData: null})
            await shiftsService.delete({ids: [deletedItem.shiftId]})
            await this.getSettings()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onChangeNewItemData = (key: string, value: any) => {
        const {newItemData} = this.state
        newItemData[key] = value
        const newData = this.props.data.map((item) => {
            if (item.id === newItemData.id) {
                item.nameHtml = this.renderNameInput()
                item.startTimeHtml = this.renderTimeInput("startTime")
                item.endTimeHtml = this.renderTimeInput("endTime")
                item.isActiveHtml = this.renderIsActiveHtml()
                item.programsHtml = this.renderProgramsInput()
                item.programVersionsHtml = this.renderProgramVersionsInput()
                item.campusesHtml = this.renderCampusesInput()
                return item
            }
            return item
        })
        this.setState({newItemData})
        this.props.dispatch({data: newData})
    }

    renderName = (name: string, workWeek: WorkDay[]) => {
        return (
            <div className={styles.inputWrap}>
                <div className={styles.name}>{name}</div>
                {this.renderWorkWeek({disabled: true, workWeek})}
            </div>
        )
    }

    renderWorkWeek = (payload?: {disabled: boolean; workWeek: WorkDay[]}) => {
        const {disabled, workWeek: data} = payload ?? {}
        const newItemData = this.state.newItemData || {}
        const workWeek = data ? data : newItemData.workWeek ?? INITIAL_WORK_WEEK

        return (
            <Collapse>
                <Panel header="Define WorkDays & Time" key="1">
                    <div className={disabled ? styles.workWeekName : styles.workweekContainer}>
                        {workWeek.map((item, index) => {
                            return (
                                <WorkDayItem
                                    key={index}
                                    item={item}
                                    onChange={(_workDay) => {
                                        const _workWeek = [...workWeek]
                                        _workWeek[index] = _workDay
                                        this.onChangeNewItemData("workWeek", _workWeek)
                                    }}
                                />
                            )
                        })}
                    </div>
                </Panel>
            </Collapse>
        )
    }

    renderNameInput = () => {
        const newItemData = this.state.newItemData || {}

        return (
            <div className={styles.inputWrap}>
                <BaseInput
                    value={newItemData.nameData}
                    placeholder="Name"
                    onChange={(value) => this.onChangeNewItemData("nameData", value)}
                />
                {this.renderWorkWeek()}
            </div>
        )
    }

    renderTimeInput = (key: "startTime" | "endTime") => {
        const newItemData = this.state.newItemData || {}
        const todayStr = moment().format("YYYY-MM-DD")
        const startTime = newItemData["startTime"] ? moment(todayStr + " " + newItemData["startTime"]) : undefined
        const endTime = newItemData["endTime"] ? moment(todayStr + " " + newItemData["endTime"]) : undefined
        const format = this.props.model.getUserTimeFormat()
        return (
            <BaseTimePicker
                key={key}
                value={key === "startTime" ? startTime : endTime}
                onChange={(newValue) => {
                    const data = newValue ? moment(newValue).format(format) : null
                    const value = newValue ? moment(newValue) : null

                    if (
                        value &&
                        ((key === "startTime" && endTime && value.isAfter(endTime)) ||
                            (key === "endTime" && startTime && startTime.isAfter(value)))
                    ) {
                        this.onChangeNewItemData(key === "startTime" ? "endTime" : "startTime", data)
                    }

                    this.onChangeNewItemData(key, data)
                }}
                disabledHours={key === "endTime" ? () => getDisabledEndHours(startTime) : undefined}
                disabledMinutes={key === "endTime" ? () => getDisabledEndMinutes(startTime, endTime) : undefined}
                placeholder={key === "startTime" ? "From" : "To"}
                format={format}
                className={styles.timePicker}
                size="small"
            />
        )
    }

    renderCampusesInput = (data?: any) => {
        const newItemData = this.state.newItemData || {}
        return (
            <div className={styles.inputWrap}>
                <div style={{width: "250px"}}>
                    <CampusSelect
                        isDisabled={!!data}
                        displayOnlyActiveCampuses
                        className={styles.select}
                        isClearable
                        isMulti
                        value={data ? data : newItemData.campuses}
                        onChange={(data) => {
                            const campusIds = data?.map((item) => item.id)
                            const newPrograms = newItemData.programs?.filter((item) => {
                                return item.campusIds?.some((campusId) => campusIds?.includes(campusId))
                            })
                            this.onChangeNewItemData("programs", newPrograms)
                            this.onChangeNewItemData("campuses", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    renderProgramsInput = (data?: any) => {
        const newItemData = this.state.newItemData || {}
        const campusIds = newItemData.campuses?.map((item) => item.id)

        return (
            <div className={styles.inputWrap}>
                <div style={{width: "250px"}}>
                    <ProgramSelect
                        isDisabled={!!data}
                        className={styles.select}
                        isClearable
                        isActive
                        campusId={campusIds}
                        isMulti
                        value={data ? data : newItemData.programs}
                        onChange={(data) => {
                            this.onChangeNewItemData("programs", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    renderProgramVersionsInput = (data?: any) => {
        const newItemData = this.state.newItemData || {}
        const programIds = newItemData.programs?.map((item) => item.id)

        return (
            <div className={styles.inputWrap}>
                <div style={{width: "250px"}}>
                    <ProgramVersionSelect
                        isDisabled={!!data}
                        className={styles.select}
                        isClearable
                        isActive
                        programId={programIds}
                        isMulti
                        value={data ? data : newItemData.programVersions}
                        onChange={(data) => {
                            this.onChangeNewItemData("programVersions", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    onClickAddItem = () => {
        const newItem = {
            id: uuid(),
            nameHtml: this.renderNameInput(),
            startTimeHtml: this.renderTimeInput("startTime"),
            endTimeHtml: this.renderTimeInput("endTime"),
            isActive: false,
            isActiveHtml: this.renderIsActiveHtml(),
            campusesHtml: this.renderCampusesInput(),
            workWeek: INITIAL_WORK_WEEK,
            programsHtml: this.renderProgramsInput(),
            programVersionsHtml: this.renderProgramVersionsInput(),
            isForm: true
        }
        const {data} = this.props
        data.push(newItem)
        this.setState({newItemData: newItem, oldItemData: null})
        this.props.dispatch({data})
    }

    validateDataBeforeSubmit = () => {
        const {isActive, nameData, startTime, endTime} = this.state.newItemData
        const {t} = this.props
        let error: string

        if (!nameData) {
            error = t("common:validation.cantEmpty", {
                field: t("settings.name")
            })
        }

        if (!isBoolean(isActive)) {
            error = t("common:validation.cantEmpty", {
                field: t("settings.isActive")
            })
        }

        if (error) {
            toastError(error)
            return false
        }
        return true
    }

    onClickSave = async () => {
        if (!this.validateDataBeforeSubmit()) {
            return
        }
        const {nameData, startTime, endTime, isActive, isForm, id, programs, programVersions, workWeek, campuses} =
            this.state.newItemData
        const workdays = workWeek.filter((x) => x.enabled === true)
        if (isForm) {
            const submitData = {
                name: nameData,
                startTime: startTime || undefined,
                endTime: endTime || undefined,
                isActive,
                programIds: programs?.map((p) => p.id) || [],
                campusIds: campuses?.map((c) => c.id) || [],
                programVersionIds: programVersions?.map((p) => p.id) || [],
                workWeek: []
            }
            submitData.workWeek = workdays.map((x) => ({
                dayOfWeek: x.dayOfWeek,
                allDay: x.allDay,
                startTime: x.startTime?.format("HH:mm"),
                endTime: x.endTime?.format("HH:mm")
            }))
            await this.createSetting(submitData)
        } else {
            const submitData = {
                name: nameData,
                startTime: startTime || undefined,
                endTime: endTime || undefined,
                isActive,
                programIds: programs?.map((p) => p.id) || [],
                campusIds: campuses?.map((c) => c.id) || [],
                programVersionIds: programVersions?.map((p) => p.id) || [],
                workWeek: []
            }
            submitData.workWeek = workdays.map((x) => ({
                dayOfWeek: x.dayOfWeek,
                allDay: x.allDay,
                startTime: x.startTime?.format("HH:mm"),
                endTime: x.endTime?.format("HH:mm")
            }))
            await this.updateSetting(id, submitData)
        }
        this.setState({newItemData: null, oldItemData: null}, () => {
            this.getSettings()
        })
    }

    createSetting = async (data) => {
        try {
            this.props.dispatch({isLoading: true})
            this.setState({isSubmitting: true})
            await shiftsService.create(data)
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
            this.setState({isSubmitting: false})
        }
    }

    updateSetting = async (shiftId, data) => {
        try {
            this.props.dispatch({isLoading: true})
            this.setState({isSubmitting: true})
            await shiftsService.update({...data, shiftId})
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
            this.setState({isSubmitting: false})
        }
    }

    onClickCancel = () => {
        const {oldItemData} = this.state
        const newData = this.props.data
            .filter((item) => !item.isForm)
            .map((item) => {
                if (oldItemData && oldItemData.id === item.id) {
                    item.nameHtml = this.renderName(oldItemData.name, oldItemData.workWeek)
                    item.startTimeHtml = oldItemData.startTime
                    item.endTimeHtml = oldItemData.endTime
                    item.isActive = oldItemData.isActive
                    item.isActiveHtml = this.renderIsActiveHtml(item)
                    item.programsHtml = this.renderProgramsInput(item.programs)
                    item.programVersionsHtml = this.renderProgramVersionsInput(item.programVersions)
                    item.campusesHtml = this.renderCampusesInput(item.campuses)
                    return item
                }
                return item
            })
        this.setState({newItemData: null, oldItemData: null})
        this.props.dispatch({data: newData})
    }

    render() {
        const {newItemData, isSubmitting} = this.state
        const {
            t,
            page,
            total,
            pageSize,
            columns,
            data,
            menuActions,
            allFields,
            fields,
            tableHeaderActions,
            isLoading,
            isHideMenuActions,
            isShowTableHeaderAction
        } = this.props

        return (
            <div className={styles.wrapper}>
                <KlassappTableHeader
                    isShowAction={isShowTableHeaderAction}
                    actions={tableHeaderActions}
                    page={page}
                    total={total}
                    defaultPageSize={pageSize}
                    onChangePage={this.props.onChangePage}
                    onChangeRowPerPage={this.props.onChangeRowPerPage}
                    fields={fields}
                    allFields={allFields}
                    onChangeFields={this.props.onChangeFields}
                    onChangeAllFields={this.props.onChangeAllFields}
                    onDraggableColumn={this.props.onDraggableColumn}
                />
                <KlassappTable
                    columns={columns}
                    data={data}
                    menuActions={isHideMenuActions ? [] : menuActions}
                    isLoading={isLoading}
                    fields={fields}
                    allFields={allFields}
                    isShowCheckedColumn
                    tableClassName={styles.studentTable}
                    onChangeFields={this.props.onChangeFields}
                    onUpdateRowData={this.props.onUpdateRowData}
                    onUpdateTableData={this.props.onUpdateTableData}
                    onClickSortColumn={this.props.onClickSortColumn}
                    onDraggableColumn={this.props.onDraggableColumn}
                    onChangeAllFields={this.props.onChangeAllFields}
                />
                {!newItemData ? (
                    !isLoading && <AddItemCircleButton onClick={this.onClickAddItem} />
                ) : (
                    <div className={styles.buttonWrap}>
                        <SecondaryButton
                            title={t("common:action.cancel")}
                            onClick={this.onClickCancel}
                            className={styles.cancelBtn}
                        />
                        <BaseButton
                            title={t("common:action.save").toUpperCase()}
                            onClick={this.onClickSave}
                            loading={isSubmitting}
                        />
                    </div>
                )}
            </div>
        )
    }
}

export const Shift = KlassappTableHOC(withTranslation(["academics", "common"])(ShiftTab))
